import {
  ARC_VOICE_CANCEL_TOOL_NAME,
  ARC_VOICE_GO_TO_TOOL_NAME,
  ArcVoiceSpeaker,
  IArcVoiceSession,
} from '@common/interfaces/arcVoice'
import {
  createAnalyser,
  createLiveAudioContext,
  LEVEL_WINDOW,
  readLevel,
  rmsLevel,
} from '../audio/levels'
import { routeOutput } from '../audio/output'
import { EchoGate, IEchoGateTally } from './echoGate'
import { floatToPcm16Base64, pcm16Base64ToFloat, resample } from './pcm'
import { isQuestion, shouldAnswerInRoom } from './roomTurns'

const CAPTURE_WORKLET_URL = '/arc-voice-capture-worklet.js'

/** What the line is doing right now, as far as the audio goes. */
export enum ArcVoiceActivity {
  Listening = 'listening',
  /** The caller is talking. */
  Hearing = 'hearing',
  Speaking = 'speaking',
}

/** "(Silence)", "[waiting for results]": the voice narrating itself, never meant to be heard. */
const STAGE_DIRECTION = /^\s*[([]/
export const isStageDirection = (text: string): boolean => STAGE_DIRECTION.test(text)

export interface IArcVoiceCaption {
  /** The provider's item id, so a cumulative transcript replaces its own line. */
  id: string
  speaker: ArcVoiceSpeaker
  text: string
  isFinal: boolean
}

export interface IArcVoiceLevels {
  /** 0–1, the caller's microphone. */
  input: number
  /** 0–1, Arc's voice. */
  output: number
}

export interface IRealtimeVoiceHandlers {
  onReady: () => void
  onActivityChange: (activity: ArcVoiceActivity) => void
  onCaption: (caption: IArcVoiceCaption) => void
  /** The provider's id for this conversation, which a later connection resumes. */
  onConversationId: (id: string) => void
  onToolCall: (name: string, args: Record<string, unknown>) => Promise<unknown>
  /** Nobody is talking and nothing is being said: a good moment to speak up. */
  onIdle: () => void
  /** The call can't carry on; the caller should be told. */
  onError: (message: string) => void
  /** Something went wrong that the session survives. */
  onWarning: (message: string) => void
  /** The provider closed the socket; not called for `hangUp()`. */
  onDisconnect: () => void
  /** `hangUp()` closed the line from this side. */
  onHangUp?: () => void
}

interface IServerEvent {
  type: string
  delta?: string
  transcript?: string
  item_id?: string
  response_id?: string
  name?: string
  call_id?: string
  arguments?: string
  conversation?: { id?: string }
  error?: { message?: string; code?: string }
}

interface IQueuedReply {
  event: object
  retries: number
}

const parseToolArguments = (raw: string | undefined): Record<string, unknown> => {
  try {
    const parsed: unknown = JSON.parse(raw ?? '{}')
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/** No `session.updated` this long after the socket opens: the session never started. */
const READY_TIMEOUT_MS = 8 * 1000
/** A connection that opens without starting a session is retried this many times. */
const READY_RETRIES = 1

/** After Arc stops talking, the speakers' tail stays out of the mic this long. */
const ECHO_TAIL_S = 0.3

/** A reply asked for and not started this long after is treated as never coming. */
const RESPONSE_START_TIMEOUT_MS = 5 * 1000

/** A reply refused because another was already running. */
const REPLY_BUSY_CODE = 'conversation_already_has_active_response'
/** Provider errors that only mean a request raced the one in flight; nothing to report. */
const BENIGN_ERROR_CODES = new Set([REPLY_BUSY_CODE, 'response_cancel_not_active'])
/** The same races as the provider words them when it sends no code. */
const BENIGN_ERROR_MESSAGE = /no active response|already has an active response/i

const isBenignError = (error: IServerEvent['error']): boolean =>
  BENIGN_ERROR_CODES.has(error?.code ?? '') || BENIGN_ERROR_MESSAGE.test(error?.message ?? '')

const isReplyBusyError = (error: IServerEvent['error']): boolean =>
  error?.code === REPLY_BUSY_CODE || /already has an active response/i.test(error?.message ?? '')
/** A refused reply is asked for again this many times before it is let go. */
const REPLY_RETRIES = 3

/** What to tell the voice when a request it handed off has started. */
const TOOL_STARTED_INSTRUCTIONS =
  "Say in a few words that you're on it, then stop. Do not repeat or re-tell any result you have already given them."

/** What to tell the voice once a cancel returns: what it stopped is the whole reply, so it is always said. */
const CANCEL_DONE_INSTRUCTIONS =
  'Say in one short sentence what the cancel returned, then stop. Do not call any tool.'

/** A quiet two-note pulse every couple of seconds while Arc works, low enough not to trip the VAD. */
const CUE_INTERVAL_MS = 2200
const CUE_GAIN = 0.025
const CUE_NOTES_HZ = [392, 523.25]

/**
 * One connection to the voice provider over the OpenAI-Realtime-style event
 * protocol that xAI's voice API speaks. Streams the mic up as PCM16, plays the
 * voice back as it arrives, captions both sides, and runs the voice model's tool
 * calls through `onToolCall`. A paused call closes its connection; resuming opens
 * a new one with the same conversation id.
 */
export class RealtimeVoiceClient {
  private readonly audioContext: AudioContext

  private readonly output: GainNode

  private readonly outputAnalyser: AnalyserNode

  private inputAnalyser: AnalyserNode | null = null

  private readonly levelBuffer: Float32Array<ArrayBuffer> = new Float32Array(LEVEL_WINDOW)

  private socket: WebSocket | null = null

  private stream: MediaStream | null = null

  private capture: AudioWorkletNode | null = null

  private playing = new Set<AudioBufferSourceNode>()

  private playbackEndsAt = 0

  private outputRate = 24_000

  private isUserSpeaking = false

  /** Their mic is muted: none of it is sent. */
  private isMuted = false

  private isResponding = false

  private arcCaptions = new Map<string, string>()

  private cueTimer: ReturnType<typeof setInterval> | null = null

  private session: IArcVoiceSession | null = null

  private pageContext: string | null = null

  private agendaBriefing: string | null = null

  private screenText: string | null = null

  private isClosed = false

  /** `session.updated` has arrived: the provider is actually listening. */
  private isReady = false

  private readyTimer: ReturnType<typeof setTimeout> | null = null

  private readyRetries = 0

  /** The reply in flight has said something out loud. */
  private hasReplySpoken = false

  /** The caller talked over a reply: what is still arriving of it is dropped. */
  private isDiscardingReply = false

  /** `response.create`s waiting for the reply in flight to finish. */
  private readonly queuedResponses: IQueuedReply[] = []

  /** The last reply asked for: the one a refusal is about, so it can be asked for again. */
  private lastSentReply: IQueuedReply | null = null

  private echoGate = new EchoGate()

  /** When Arc last finished asking the room a question, for a huddle's follow-up window. */
  private arcAskedAt: number | null = null

  private didLastReplyAsk = false

  /** Replies asked for and not yet started; in a huddle, any other reply is the provider's own. */
  private requestedReplies = 0

  /** The reply in flight is one the provider started by itself in a huddle: cancelled, unheard. */
  private isUnrequestedReply = false

  private isAwaitingResponseStart = false

  private responseStartTimer: ReturnType<typeof setTimeout> | null = null

  /** Built inside the click that starts the call: Safari starts a context made later suspended. */
  constructor(private readonly handlers: IRealtimeVoiceHandlers) {
    this.audioContext = createLiveAudioContext()
    this.output = this.audioContext.createGain()
    this.outputAnalyser = createAnalyser(this.audioContext)
    this.output.connect(this.outputAnalyser).connect(this.audioContext.destination)
  }

  /** What the mic heard while Arc talked on this connection. */
  get audioTally(): IEchoGateTally {
    return this.echoGate.tally
  }

  /**
   * Mid-sentence on either side, or still answering. In a huddle someone is nearly always
   * talking, so only Arc's own reply counts: waiting for quiet there means never speaking.
   */
  get isBusy(): boolean {
    return (!this.isRoom && this.isUserSpeaking) || this.isResponding || this.isPlaying
  }

  get levels(): IArcVoiceLevels {
    return {
      input: this.isMuted ? 0 : readLevel(this.inputAnalyser, this.levelBuffer),
      output: this.isPlaying ? readLevel(this.outputAnalyser, this.levelBuffer) : 0,
    }
  }

  private get isPlaying(): boolean {
    return this.audioContext.currentTime < this.playbackEndsAt
  }

  private get isRoom(): boolean {
    return !!this.session?.isRoom
  }

  /** Play Arc through another output; null is the system default. */
  setOutputDevice(outputId: string | null): void {
    routeOutput(this.audioContext, outputId)
  }

  /** Arc's voice as a stream, for a huddle to send to the others in the room. */
  captureOutput(): MediaStream {
    const destination = this.audioContext.createMediaStreamDestination()
    this.output.connect(destination)
    return destination.stream
  }

  async connect(
    session: IArcVoiceSession,
    microphone: MediaStream,
    resumeConversationId: string | null,
    isRedial = false,
  ): Promise<void> {
    this.stream = microphone
    // Outside a click a browser may start the audio suspended (Safari does); a redial
    // that can't wake it would be a call with no sound, so it pauses for a click instead.
    if (isRedial && this.audioContext.state !== 'running') {
      await this.audioContext.resume().catch(() => {})
      // Read again: TypeScript narrowed `state` before the await.
      if ((this.audioContext.state as AudioContextState) !== 'running') {
        throw new Error('Lost the line to Arc. Resume to carry on.')
      }
    }
    this.session = session
    this.echoGate = new EchoGate(session.cutInLevel)
    this.pageContext ??= session.pageContext
    this.outputRate = session.sampleRate
    await this.audioContext.audioWorklet.addModule(CAPTURE_WORKLET_URL)
    if (this.isClosed) return

    const url = resumeConversationId
      ? `${session.url}&conversation_id=${encodeURIComponent(resumeConversationId)}`
      : session.url
    this.openSocket(url, session)
    this.startCapture(session.sampleRate)
  }

  /**
   * A socket that opens isn't a session that started: xAI's own client waits for
   * `session.updated`, and retries the roughly one connection in ten that opens
   * without one. Until then nothing is reported as listening and no audio is sent.
   */
  private openSocket(url: string, session: IArcVoiceSession): void {
    const socket = new WebSocket(url, session.protocols)
    this.socket = socket
    socket.onopen = () => {
      this.configure(session)
      this.readyTimer = setTimeout(() => this.handleNotReady(url, session), READY_TIMEOUT_MS)
    }
    socket.onmessage = (event: MessageEvent<string>) => this.handleEvent(event.data)
    socket.onclose = () => {
      if (this.isClosed || this.socket !== socket) return
      this.handlers.onDisconnect()
    }
  }

  private handleNotReady(url: string, session: IArcVoiceSession): void {
    if (this.isReady || this.isClosed) return
    const stale = this.socket
    if (this.readyRetries < READY_RETRIES) {
      this.readyRetries += 1
      this.openSocket(url, session)
      stale?.close()
      return
    }
    this.socket = null
    stale?.close()
    this.handlers.onDisconnect()
  }

  /** Tell the voice which page the caller is on now; it is part of its standing brief. */
  setPageContext(pageContext: string | null): void {
    this.pageContext = pageContext
    this.refreshInstructions()
  }

  /** Tell the voice what is on the caller's screen; it is part of its standing brief. */
  setScreenText(screenText: string | null): void {
    if (screenText === this.screenText) return
    this.screenText = screenText
    this.refreshInstructions()
  }

  /** Tell the voice where the call's agenda stands; it is part of its standing brief. */
  setAgenda(briefing: string | null): void {
    if (briefing === this.agendaBriefing) return
    this.agendaBriefing = briefing
    this.refreshInstructions()
  }

  private refreshInstructions(): void {
    if (this.session)
      this.send({ type: 'session.update', session: { instructions: this.instructions } })
  }

  /**
   * Ask the voice model for one turn, steered by `instructions`. A reply's own
   * instructions take the place of the session's, so the standing brief (who it
   * is, the page, the agenda) goes with them.
   */
  prompt(instructions: string): void {
    this.requestResponse({
      type: 'response.create',
      response: { instructions: `${this.instructions}\n\nFor this reply: ${instructions}` },
    })
  }

  /** The reply in flight is over, done or never started: send the next one waiting, if any. */
  private finishResponse(): void {
    this.isResponding = false
    const next = this.queuedResponses.shift()
    if (next) this.sendReply(next)
    else this.settleIfQuiet()
  }

  /** One reply at a time: a second `response.create` while one runs is rejected by the provider. */
  private requestResponse(event: object): void {
    const reply = { event, retries: 0 }
    if (this.isResponding) {
      this.queuedResponses.push(reply)
      return
    }
    this.sendReply(reply)
  }

  /**
   * The provider refused the reply because one of its own was already running
   * (its turn detection answers speech by itself). Ask again once that one is
   * done, ahead of anything queued: a result refused this way was otherwise lost
   * while counted as told.
   */
  private retryRefusedReply(): void {
    const reply = this.lastSentReply
    if (!reply) return
    this.lastSentReply = null
    this.requestedReplies = Math.max(0, this.requestedReplies - 1)
    if (reply.retries < REPLY_RETRIES) {
      this.queuedResponses.unshift({ ...reply, retries: reply.retries + 1 })
    }
    // The line stays busy with the provider's own reply, whose `response.done` sends
    // this one; if that reply's start hasn't been seen yet, the timeout still covers it.
    if (this.isAwaitingResponseStart) this.armResponseStartTimer()
  }

  private sendReply(reply: IQueuedReply): void {
    this.isResponding = true
    this.requestedReplies += 1
    this.lastSentReply = reply
    this.send(reply.event)
    this.armResponseStartTimer()
  }

  private armResponseStartTimer(): void {
    // Marked busy before the provider confirms, so a reply asked for that never starts
    // (rejected, or lost) mustn't hold the line busy for good.
    if (this.responseStartTimer) clearTimeout(this.responseStartTimer)
    this.responseStartTimer = setTimeout(() => {
      if (!this.isAwaitingResponseStart) return
      this.isAwaitingResponseStart = false
      this.requestedReplies = Math.max(0, this.requestedReplies - 1)
      this.finishResponse()
    }, RESPONSE_START_TIMEOUT_MS)
    this.isAwaitingResponseStart = true
  }

  addContext(text: string): void {
    this.send({
      type: 'conversation.item.create',
      item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
    })
  }

  /**
   * Mute or unmute their mic. Either way, what the provider and the echo gate were
   * still holding is dropped, so nothing said before or during a mute is heard after it.
   */
  setMuted(isMuted: boolean): void {
    if (isMuted === this.isMuted) return
    this.isMuted = isMuted
    this.echoGate.reset()
    this.send({ type: 'input_audio_buffer.clear' })
    if (!isMuted || !this.isUserSpeaking) return
    // Cut off mid-sentence: the provider will never say they stopped, so say it here.
    this.isUserSpeaking = false
    this.handlers.onActivityChange(ArcVoiceActivity.Listening)
    this.settleIfQuiet()
  }

  setThinkingCue(isOn: boolean): void {
    if (!isOn || this.isClosed) {
      if (this.cueTimer) clearInterval(this.cueTimer)
      this.cueTimer = null
      return
    }
    if (this.cueTimer) return
    this.cueTimer = setInterval(() => {
      if (!this.isBusy) this.playCue()
    }, CUE_INTERVAL_MS)
  }

  hangUp(): void {
    if (this.isClosed) return
    this.isClosed = true
    this.handlers.onHangUp?.()
    if (this.readyTimer) clearTimeout(this.readyTimer)
    if (this.responseStartTimer) clearTimeout(this.responseStartTimer)
    this.setThinkingCue(false)
    this.socket?.close()
    this.stopPlayback()
    this.capture?.disconnect()
    this.stream?.getTracks().forEach((track) => track.stop())
    void this.audioContext.close()
  }

  private get instructions(): string {
    // A phone's call is offered no way to move the screen (see StartArcVoice).
    const canGoTo = !!this.session?.tools.some(({ name }) => name === ARC_VOICE_GO_TO_TOOL_NAME)
    return [
      this.session?.instructions ?? '',
      ...(this.pageContext
        ? [
            `What they are looking at in Atlas right now (use it for "this", "here", "this client"): ${this.pageContext}`,
          ]
        : []),
      ...(this.screenText
        ? [
            `The text on their screen right now, top to bottom (rows of a table read in order; it may be cut off). Answer questions about what they can see from it.${canGoTo ? ` To open a row or link listed under [Links on screen], call ${ARC_VOICE_GO_TO_TOOL_NAME} with its url.` : ''} When you hand work off about a row or link there, pass its link or id in the request:\n${this.screenText}`,
          ]
        : []),
      ...(this.agendaBriefing ? [this.agendaBriefing] : []),
    ].join('\n\n')
  }

  private send(event: object): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(event))
  }

  private configure(session: IArcVoiceSession): void {
    const format = { type: 'audio/pcm', rate: session.sampleRate }
    this.send({
      type: 'session.update',
      session: {
        voice: session.voice,
        instructions: this.instructions,
        // It only holds the conversation; Arc does the thinking.
        reasoning: { effort: 'none' },
        turn_detection: session.isRoom
          ? { type: 'server_vad', create_response: false, interrupt_response: false }
          : { type: 'server_vad' },
        resumption: { enabled: true },
        audio: {
          input: { format, transcription: { model: session.transcriptionModel } },
          output: { format, speed: session.speechSpeed },
        },
        tools: session.tools.map((tool) => ({ type: 'function', ...tool })),
      },
    })
  }

  private handleSessionUpdated(): void {
    if (this.isReady) return
    this.isReady = true
    if (this.readyTimer) clearTimeout(this.readyTimer)
    this.handlers.onActivityChange(ArcVoiceActivity.Listening)
    this.handlers.onReady()
  }

  /**
   * Whether this frame of mic audio goes to the provider. Not before the session
   * is up, and not while Arc's voice is coming out of the speakers (or just
   * after), unless the caller is plainly talking over it: echo cancellation alone
   * still let the model hear itself, which is xAI's own client's reason for this.
   */
  private micToSend(samples: Float32Array): Float32Array[] {
    if (!this.isReady || this.isMuted) return []
    const { send, isBargeIn } = this.echoGate.decide({
      samples,
      level: rmsLevel(samples),
      at: this.audioContext.currentTime,
      playbackEndsAt: this.playbackEndsAt,
      echoEndsAt: this.playbackEndsAt + ECHO_TAIL_S,
    })
    // Stopped here rather than when the provider notices: that waits on audio the gate held back.
    if (isBargeIn) this.interruptReply()
    return send
  }

  /** Cut a reply off unheard: cancel what is still coming and stop what is playing. */
  private dropReply(): void {
    this.isDiscardingReply = true
    if (this.isResponding && !this.isAwaitingResponseStart) this.send({ type: 'response.cancel' })
    this.stopPlayback()
  }

  /** They talked over the answer: stop it, the way a person would, and drop the rest of it. */
  private interruptReply(): void {
    if (this.isResponding || this.isPlaying) this.isDiscardingReply = true
    // Audio outruns playback, so the reply has often finished generating by now and
    // there is nothing left to cancel; only one the provider has actually started is.
    if (this.isResponding && !this.isAwaitingResponseStart) this.send({ type: 'response.cancel' })
    this.stopPlayback()
    this.handlers.onActivityChange(ArcVoiceActivity.Hearing)
  }

  private startCapture(sampleRate: number): void {
    if (!this.stream) return
    const source = this.audioContext.createMediaStreamSource(this.stream)
    this.inputAnalyser = createAnalyser(this.audioContext)
    source.connect(this.inputAnalyser)

    this.capture = new AudioWorkletNode(this.audioContext, 'arc-voice-capture')
    this.capture.port.onmessage = (event: MessageEvent<Float32Array>) => {
      this.micToSend(event.data).forEach((chunk) => {
        const samples = resample(chunk, this.audioContext.sampleRate, sampleRate)
        this.send({ type: 'input_audio_buffer.append', audio: floatToPcm16Base64(samples) })
      })
    }
    // Routed to a muted output so the browser keeps pulling audio through the tap.
    const mute = this.audioContext.createGain()
    mute.gain.value = 0
    source.connect(this.capture).connect(mute).connect(this.audioContext.destination)
  }

  private handleEvent(raw: string): void {
    let event: IServerEvent
    try {
      event = JSON.parse(raw) as IServerEvent
    } catch {
      return
    }

    switch (event.type) {
      case 'conversation.created':
        if (event.conversation?.id) this.handlers.onConversationId(event.conversation.id)
        break
      case 'session.updated':
        this.handleSessionUpdated()
        break
      case 'input_audio_buffer.speech_started':
        // Audio sent just before a mute: its speech is dropped, so it mustn't cut Arc off.
        if (this.isMuted) break
        this.isUserSpeaking = true
        // In a huddle people talk among themselves; only plainly raised speech (the echo gate) cuts in.
        if (!this.isRoom) this.interruptReply()
        break
      case 'input_audio_buffer.speech_stopped':
        this.isUserSpeaking = false
        this.handlers.onActivityChange(ArcVoiceActivity.Listening)
        // A result that landed while they talked is told now, not after Arc next speaks.
        this.settleIfQuiet()
        break
      case 'conversation.item.input_audio_transcription.updated':
        this.captionUser(event, false)
        break
      case 'conversation.item.input_audio_transcription.completed':
        this.captionUser(event, true)
        break
      case 'response.created':
        this.handleResponseCreated()
        break
      case 'response.output_audio_transcript.delta':
        if (event.delta?.trim()) this.hasReplySpoken = true
        this.captionArc(event, false)
        break
      case 'response.output_audio_transcript.done':
        this.captionArc(event, true)
        break
      case 'response.done':
        this.finishResponse()
        break
      case 'response.output_audio.delta':
      case 'response.audio.delta':
        if (event.delta && !this.isDiscardingReply) this.play(event.delta)
        break
      case 'response.function_call_arguments.done':
        if (!this.isUnrequestedReply) void this.runToolCall(event)
        break
      case 'error':
        // The session stays open after an error event; only the socket closing ends the line.
        if (isReplyBusyError(event.error)) this.retryRefusedReply()
        if (!isBenignError(event.error)) {
          this.handlers.onWarning(event.error?.message ?? 'Arc Voice ran into a problem.')
        }
        break
      default:
        break
    }
  }

  private handleResponseCreated(): void {
    this.isAwaitingResponseStart = false
    this.hasReplySpoken = false
    this.isResponding = true
    // A huddle's voice is told not to reply by itself; one that does anyway is cut off unheard.
    this.isUnrequestedReply = this.isRoom && this.requestedReplies === 0
    this.isDiscardingReply = this.isUnrequestedReply
    if (this.isUnrequestedReply) this.send({ type: 'response.cancel' })
    else this.requestedReplies = Math.max(0, this.requestedReplies - 1)
  }

  private captionUser(
    { item_id: itemId, transcript, delta }: IServerEvent,
    isFinal: boolean,
  ): void {
    const text = (transcript ?? delta ?? '').trim()
    if (!itemId || !text) return
    this.handlers.onCaption({ id: itemId, speaker: ArcVoiceSpeaker.User, text, isFinal })
    // A huddle's voice never replies on its own (see configure): it is asked to, when addressed.
    if (isFinal && this.isRoom && shouldAnswerInRoom(text, Date.now(), this.arcAskedAt)) {
      this.requestResponse({ type: 'response.create' })
    }
  }

  private captionArc(event: IServerEvent, isDone: boolean): void {
    const id = event.item_id ?? event.response_id
    if (!id || this.isUnrequestedReply) return
    const text = isDone
      ? (event.transcript ?? this.arcCaptions.get(id) ?? '')
      : `${this.arcCaptions.get(id) ?? ''}${event.delta ?? ''}`
    // In a huddle a reply opening on an aside is the voice narrating itself; cut before it's heard.
    if (this.isRoom && isStageDirection(text)) {
      this.dropReply()
      return
    }
    if (isDone) {
      this.arcCaptions.delete(id)
      this.didLastReplyAsk = isQuestion(text)
    } else this.arcCaptions.set(id, text)
    if (text.trim()) {
      this.handlers.onCaption({ id, speaker: ArcVoiceSpeaker.Arc, text, isFinal: isDone })
    }
  }

  private play(base64: string): void {
    const samples = pcm16Base64ToFloat(base64)
    if (samples.length === 0) return
    const buffer = this.audioContext.createBuffer(1, samples.length, this.outputRate)
    buffer.copyToChannel(samples, 0)

    const source = this.audioContext.createBufferSource()
    source.buffer = buffer
    source.connect(this.output)
    const startAt = Math.max(this.audioContext.currentTime, this.playbackEndsAt)
    source.start(startAt)
    this.playbackEndsAt = startAt + buffer.duration
    this.playing.add(source)
    source.onended = () => {
      this.playing.delete(source)
      if (this.playing.size === 0) {
        this.arcAskedAt = this.didLastReplyAsk ? Date.now() : null
        this.handlers.onActivityChange(ArcVoiceActivity.Listening)
        this.settleIfQuiet()
      }
    }
    this.handlers.onActivityChange(ArcVoiceActivity.Speaking)
  }

  private settleIfQuiet(): void {
    if (!this.isBusy && this.playing.size === 0) this.handlers.onIdle()
  }

  private stopPlayback(): void {
    this.playing.forEach((source) => {
      source.onended = null
      source.stop()
    })
    this.playing.clear()
    this.playbackEndsAt = 0
  }

  private playCue(): void {
    const now = this.audioContext.currentTime
    CUE_NOTES_HZ.forEach((frequency, index) => {
      const oscillator = this.audioContext.createOscillator()
      const envelope = this.audioContext.createGain()
      const startAt = now + index * 0.18
      oscillator.frequency.value = frequency
      envelope.gain.setValueAtTime(0, startAt)
      envelope.gain.linearRampToValueAtTime(CUE_GAIN, startAt + 0.04)
      envelope.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.5)
      oscillator.connect(envelope).connect(this.audioContext.destination)
      oscillator.start(startAt)
      oscillator.stop(startAt + 0.55)
    })
  }

  private async runToolCall({ name, call_id: callId, arguments: rawArgs }: IServerEvent) {
    if (!name || !callId) return
    // It usually says "Adding that now." as it hands off; a second "On it." is noise.
    const hasAcknowledged = this.hasReplySpoken
    const output = await this.handlers.onToolCall(name, parseToolArguments(rawArgs))
    this.send({
      type: 'conversation.item.create',
      item: { type: 'function_call_output', call_id: callId, output: JSON.stringify(output) },
    })
    if (name === ARC_VOICE_CANCEL_TOOL_NAME) this.prompt(CANCEL_DONE_INSTRUCTIONS)
    else if (!hasAcknowledged) this.prompt(TOOL_STARTED_INSTRUCTIONS)
  }
}
